import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, GridBoxListItem as Item } from '@factorialco/deprecated-design-system'
import {
  FetchTimeoffDataDocument,
  PolicyFragment,
  UpdateMainTimeoffPolicyDocument,
  UpdateMainTimeoffPolicyMutation,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface Props {
  policy: PolicyFragment
  totalCount: number | null | undefined
}

const PolicyItem = ({ policy, totalCount }: React.PropsWithChildren<Props>) => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const options: Array<Record<string, any>> = []
  const main = policy.main

  const [updateMainTimeoffPolicyMutation] = useMutation(UpdateMainTimeoffPolicyDocument, {
    extractorFn: (mutationData: UpdateMainTimeoffPolicyMutation) =>
      mutationData.timeoffMutations.updateTimeoffPolicy,
  })

  const setMainPolicy = async () => {
    const result = await updateMainTimeoffPolicyMutation({
      variables: {
        id: policy.id,
        main: true,
      },
      refetchQueries: [FetchTimeoffDataDocument],
    })

    handleResult(result, () => {})
  }

  // Add option to set policy as main
  if (!main) {
    options.unshift({
      onClick: setMainPolicy,
      title: i18n.t('settings.timeoff_policies.list.item.set_as_main'),
    })
  }

  // Add option to remove
  if (totalCount && totalCount > 1) {
    options.push({
      onClick: () =>
        main
          ? null
          : navigate(navigation.settings.timeoffPolicies.removeId.fullPath({ id: policy.id })),
      title: i18n.t('settings.timeoff_policies.list.item.remove'),
      type: main ? 'primary' : 'wrong',
      disabled: main,
      description: main
        ? i18n.t('settings.timeoff_policies.list.item.remove_main_description')
        : null,
    })
  }

  // Tooltip
  const renderTooltipContent = () => {
    if (!main) return null

    return i18n.t('settings.timeoff_policies.list.item.tag_description')
  }

  // Employee count
  const renderEmployeeCount = () => {
    return (
      <span>
        {i18n.tp('settings.timeoff_policies.list.item.assigned_employees', {
          count: policy.employeesConnection.totalCount,
        })}
      </span>
    )
  }

  return (
    <Item
      actionText={i18n.t('settings.timeoff_policies.list.item.actionText')}
      illustrationName="document-folder-board-edit"
      labelText={main ? i18n.t('settings.timeoff_policies.list.item.tag') : null}
      labelTooltipContent={renderTooltipContent()}
      onClickAction={() =>
        navigate(navigation.settings.timeoffPolicies.id.details.fullPath({ id: policy.id }))
      }
      options={options}
      title={policy.name}
      extra={main ? { 'data-intercom-target': 'timeoff-policies-list-item' } : undefined}
    >
      <Box inline>{renderEmployeeCount()}</Box>
    </Item>
  )
}

export default observer(PolicyItem)
